/**
 * executionDataService.ts
 * Salva e carrega dados de execução (respostas de campos, checklist, anexos).
 * Usa tabelas v2 como destino primário, com fallback para workflow_task_responses_v9.
 */
import { supabase } from '../../../lib/supabaseClient';

// ============================================================================
// TIPOS
// ============================================================================

export interface FieldResponseRow {
  fieldId: string;
  value: unknown;
}

export interface ChecklistResponseRow {
  checklistItemId: string;
  completed: boolean;
}

// ============================================================================
// LOAD — Respostas salvas
// ============================================================================

export async function loadFieldResponses(
  taskId: string,
  orgId: string,
): Promise<Record<string, unknown>> {
  const { data, error } = await supabase
    .from('workflow_field_responses_v2')
    .select('field_id, value')
    .eq('task_id', taskId)
    .eq('organization_id', orgId);

  if (error || !data || data.length === 0) return {};

  const result: Record<string, unknown> = {};
  for (const row of data) {
    result[row.field_id] = row.value;
  }
  return result;
}

export async function loadChecklistResponses(
  taskId: string,
  orgId: string,
): Promise<Record<string, boolean>> {
  const { data, error } = await supabase
    .from('workflow_checklist_responses_v2')
    .select('checklist_item_id, completed')
    .eq('task_id', taskId)
    .eq('organization_id', orgId);

  if (error || !data || data.length === 0) return {};

  const result: Record<string, boolean> = {};
  for (const row of data) {
    result[row.checklist_item_id] = row.completed;
  }
  return result;
}

// ============================================================================
// SAVE — Campo individual (autosave)
// ============================================================================

export async function saveFieldResponse(
  taskId: string,
  orgId: string,
  fieldId: string,
  value: unknown,
  userId: string,
): Promise<void> {
  const { error } = await supabase
    .from('workflow_field_responses_v2')
    .upsert(
      {
        task_id: taskId,
        organization_id: orgId,
        field_id: fieldId,
        value: value as any,
        updated_at: new Date().toISOString(),
        updated_by: userId,
      },
      { onConflict: 'task_id,field_id' },
    );

  if (error) {
    console.error('[executionData] saveFieldResponse error:', error.message);
  }
}

// ============================================================================
// SAVE — Checklist item individual (autosave)
// ============================================================================

export async function saveChecklistResponse(
  taskId: string,
  orgId: string,
  checklistItemId: string,
  completed: boolean,
  userId: string,
): Promise<void> {
  const { error } = await supabase
    .from('workflow_checklist_responses_v2')
    .upsert(
      {
        task_id: taskId,
        organization_id: orgId,
        checklist_item_id: checklistItemId,
        completed,
        completed_at: completed ? new Date().toISOString() : null,
        completed_by: completed ? userId : null,
      },
      { onConflict: 'task_id,checklist_item_id' },
    );

  if (error) {
    console.error('[executionData] saveChecklistResponse error:', error.message);
  }
}

// ============================================================================
// SAVE — Batch (ao concluir)
// ============================================================================

export async function saveAllFieldResponses(
  taskId: string,
  orgId: string,
  formData: Record<string, unknown>,
  userId: string,
): Promise<void> {
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const entries = Object.entries(formData).filter(([fieldId]) => UUID_RE.test(fieldId));
  if (entries.length === 0) return;

  const rows = entries.map(([fieldId, value]) => ({
    task_id: taskId,
    organization_id: orgId,
    field_id: fieldId,
    value: value as any,
    updated_at: new Date().toISOString(),
    updated_by: userId,
  }));

  const { error } = await supabase
    .from('workflow_field_responses_v2')
    .upsert(rows, { onConflict: 'task_id,field_id' });

  if (error) {
    console.error('[executionData] saveAllFieldResponses error:', error.message);
  }
}

export async function saveAllChecklistResponses(
  taskId: string,
  orgId: string,
  checklistData: Record<string, boolean>,
  userId: string,
): Promise<void> {
  const entries = Object.entries(checklistData);
  if (entries.length === 0) return;

  const now = new Date().toISOString();
  const rows = entries.map(([itemId, completed]) => ({
    task_id: taskId,
    organization_id: orgId,
    checklist_item_id: itemId,
    completed,
    completed_at: completed ? now : null,
    completed_by: completed ? userId : null,
  }));

  const { error } = await supabase
    .from('workflow_checklist_responses_v2')
    .upsert(rows, { onConflict: 'task_id,checklist_item_id' });

  if (error) {
    console.error('[executionData] saveAllChecklistResponses error:', error.message);
  }
}

// ============================================================================
// ANEXOS
// ============================================================================

export async function saveAttachment(
  taskId: string,
  orgId: string,
  file: { name: string; url: string; type?: string; sizeBytes?: number },
  userId: string,
): Promise<string | null> {
  const { data, error } = await supabase
    .from('workflow_attachments_v2')
    .insert({
      task_id: taskId,
      organization_id: orgId,
      file_name: file.name,
      file_url: file.url,
      file_type: file.type ?? null,
      file_size_bytes: file.sizeBytes ?? null,
      created_by: userId,
    })
    .select('id')
    .single();

  if (error) {
    console.error('[executionData] saveAttachment error:', error.message);
    return null;
  }
  return data?.id ?? null;
}

export async function loadAttachments(
  taskId: string,
  orgId: string,
): Promise<{ id: string; fileName: string; fileUrl: string; fileType: string | null; createdAt: string }[]> {
  const { data, error } = await supabase
    .from('workflow_attachments_v2')
    .select('id, file_name, file_url, file_type, created_at')
    .eq('task_id', taskId)
    .eq('organization_id', orgId)
    .order('created_at', { ascending: false });

  if (error || !data) return [];

  return data.map((row) => ({
    id: row.id,
    fileName: row.file_name,
    fileUrl: row.file_url,
    fileType: row.file_type,
    createdAt: row.created_at,
  }));
}

// ============================================================================
// EVENT LOG
// ============================================================================

export async function logExecutionEvent(
  taskId: string,
  instanceId: string,
  orgId: string,
  userId: string,
  action: 'task_started' | 'field_updated' | 'checklist_toggled' | 'attachment_added' | 'task_completed' | 'task_paused' | 'task_resumed',
  metadata?: Record<string, unknown>,
): Promise<void> {
  const { error } = await supabase
    .from('workflow_task_logs')
    .insert({
      task_id: taskId,
      process_instance_id: instanceId,
      organization_id: orgId,
      action,
      performed_by: userId,
      metadata: metadata ?? {},
      created_at: new Date().toISOString(),
    });

  if (error) {
    // Non-blocking — log but don't throw
    console.error('[executionData] logExecutionEvent error:', error.message);
  }
}
